import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../../app/store";
import { BannerType } from "../../types/types";
import { API_URL } from "../../config";

export type UpdateBannerArgs = {
  id: string;
  patch: Record<string, unknown>;
};

export const bannersApi = createApi({
  reducerPath: "banners",
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      headers.set("Content-Type", "application/json");
      return headers;
    },
  }),
  tagTypes: ["Banner"],
  endpoints: (builder) => ({
    getBanners: builder.query<BannerType[], void>({
      query: () => ({ url: "banners", method: "GET" }),
      providesTags: ["Banner"],
    }),
    getBannerById: builder.query<BannerType, string>({
      query: (id) => `banners/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Banner", id }],
    }),
    createBanner: builder.mutation<unknown, BannerType>({
      query: (body) => ({ url: "banners", method: "POST", body }),
      invalidatesTags: ["Banner"],
    }),
    updateBanner: builder.mutation<unknown, UpdateBannerArgs>({
      query: ({ id, patch }) => ({ url: `banners/${id}`, method: "PUT", body: patch }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Banner", id }, "Banner"],
    }),
    deleteBanner: builder.mutation<{ id: string; deleted: boolean }, string>({
      query: (id) => ({ url: `banners/${id}`, method: "DELETE" }),
      invalidatesTags: (_result, _error, id) => [{ type: "Banner", id }, "Banner"],
    }),
  }),
});

export const {
  useGetBannersQuery,
  useGetBannerByIdQuery,
  useCreateBannerMutation,
  useUpdateBannerMutation,
  useDeleteBannerMutation,
} = bannersApi;

export default bannersApi;
